<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ColorPicker from 'dashboard/components-next/colorpicker/ColorPicker.vue';
import Icon from 'next/icon/Icon.vue';
import { CUSTOM_COLOR_SWATCHES } from 'dashboard/components-next/planning/Scheduler/allocationCategories';

const props = defineProps({
  modelValue: { type: String, default: null },
  fallbackColor: { type: String, default: null },
  resetLabel: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const displayColor = computed(
  () => props.modelValue || props.fallbackColor || '#80838D'
);

const isOverridden = computed(() => Boolean(props.modelValue));

const selectSwatch = swatch => {
  if (props.disabled) return;
  emit('update:modelValue', swatch);
};

const pickerColor = computed({
  get: () => displayColor.value,
  set: value => {
    if (props.disabled || !value) return;
    emit('update:modelValue', value);
  },
});

const resetColor = () => {
  if (props.disabled) return;
  emit('update:modelValue', null);
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex items-center gap-2 flex-wrap">
      <button
        v-for="swatch in CUSTOM_COLOR_SWATCHES"
        :key="swatch"
        type="button"
        class="size-7 rounded-full border-2 transition-all"
        :class="
          modelValue === swatch
            ? 'border-n-slate-12 scale-110'
            : 'border-n-weak hover:border-n-slate-8'
        "
        :style="{ backgroundColor: swatch }"
        :disabled="disabled"
        :title="swatch"
        @click="selectSwatch(swatch)"
      />
    </div>
    <div class="flex items-center gap-2">
      <ColorPicker v-model="pickerColor" />
      <button
        v-if="isOverridden"
        type="button"
        class="flex items-center justify-center size-8 rounded-full border border-n-weak hover:bg-n-slate-4 transition-colors"
        :title="resetLabel || t('PLANNING.STATUSES.COLOR.RESET')"
        :disabled="disabled"
        @click="resetColor"
      >
        <Icon
          icon="i-lucide-rotate-ccw"
          class="size-5 shrink-0 text-n-slate-11"
        />
      </button>
    </div>
  </div>
</template>
